import toast from 'react-hot-toast';

/**
 * WhatsApp Service - Frontend client for whatsapp-web.js backend
 * 
 * This service communicates with the Node.js backend that runs whatsapp-web.js
 * All WhatsApp operations are handled server-side for reliability
 */

const API_BASE = '/api';

// Interfaces
export interface WhatsAppStatus {
  status: 'connected' | 'disconnected';
  authenticated: boolean;
  phoneNumber: string | null;
  hasQR: boolean;
  error: string | null;
}

export interface SendMessageParams {
  to: string;
  message: string;
  type?: 'text' | 'image' | 'document';
}

export interface Message {
  id: string;
  from: string;
  fromName: string;
  body: string;
  timestamp: string;
  isGroup: boolean;
  groupName?: string;
}

export interface Contact {
  id: string;
  number: string;
  name: string;
  isGroup: boolean;
}

export interface Group {
  id: string;
  name: string;
  participantCount: number;
}

export interface Flow {
  id: string;
  name: string;
  description?: string;
  nodes: any[];
  edges: any[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  stats: {
    executions: number;
    successRate: number;
    lastRun: string | null;
  };
}

export interface Log {
  id: string;
  timestamp: string;
  level: 'info' | 'warning' | 'error';
  message: string;
  details?: any;
}

class WhatsAppService {
  private statusPollingInterval: ReturnType<typeof setInterval> | null = null;
  private currentStatus: WhatsAppStatus | null = null;
  private statusListeners: ((status: WhatsAppStatus) => void)[] = [];

  constructor() {
    this.startStatusPolling();
  }

  // ============================================
  // STATUS & CONNECTION
  // ============================================

  async getStatus(): Promise<WhatsAppStatus> {
    try {
      const response = await fetch(`${API_BASE}/status`);
      const data = await response.json();
      this.currentStatus = data;
      this.notifyStatusListeners(data);
      return data;
    } catch (error) {
      console.error('Error getting status:', error);
      const errorStatus: WhatsAppStatus = {
        status: 'disconnected',
        authenticated: false,
        phoneNumber: null,
        hasQR: false,
        error: 'Connection failed'
      };
      this.currentStatus = errorStatus;
      return errorStatus;
    }
  }

  async getQRCode(): Promise<string | null> {
    try {
      const response = await fetch(`${API_BASE}/qr`);
      const data = await response.json();
      if (data.success && data.qr) {
        return data.qr;
      }
      return null;
    } catch (error) {
      console.error('Error getting QR code:', error);
      return null;
    }
  }

  async testConnection(): Promise<boolean> {
    const status = await this.getStatus();
    return status.status === 'connected' && status.authenticated;
  }

  onStatusChange(callback: (status: WhatsAppStatus) => void) {
    this.statusListeners.push(callback);
    if (this.currentStatus) {
      callback(this.currentStatus);
    }
  }

  removeStatusListener(callback: (status: WhatsAppStatus) => void) {
    this.statusListeners = this.statusListeners.filter(l => l !== callback);
  }

  private notifyStatusListeners(status: WhatsAppStatus) {
    this.statusListeners.forEach(listener => listener(status));
  }

  private startStatusPolling() {
    this.statusPollingInterval = setInterval(() => {
      this.getStatus();
    }, 5000);
    this.getStatus();
  }

  stopStatusPolling() {
    if (this.statusPollingInterval) {
      clearInterval(this.statusPollingInterval);
      this.statusPollingInterval = null;
    }
  }

  // ============================================
  // MESSAGING
  // ============================================

  async sendMessage(params: SendMessageParams): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Messaggio inviato');
        return true;
      } else {
        toast.error(`Errore: ${result.error}`);
        return false;
      }
    } catch (error: any) {
      console.error('Error sending message:', error);
      toast.error(`Errore invio: ${error.message}`);
      return false;
    }
  }

  async getMessages(limit: number = 50): Promise<Message[]> {
    try {
      const response = await fetch(`${API_BASE}/messages?limit=${limit}`);
      const data = await response.json();
      return data.messages || [];
    } catch (error) {
      console.error('Error getting messages:', error);
      return [];
    }
  }

  // ============================================
  // CONTACTS & GROUPS
  // ============================================

  async getContacts(): Promise<Contact[]> {
    try {
      const response = await fetch(`${API_BASE}/contacts`);
      const data = await response.json();
      return data.contacts || [];
    } catch (error) {
      console.error('Error getting contacts:', error);
      return [];
    }
  }

  async getGroups(): Promise<Group[]> {
    try {
      const response = await fetch(`${API_BASE}/groups`);
      const data = await response.json();
      return data.groups || [];
    } catch (error) {
      console.error('Error getting groups:', error);
      return [];
    }
  }

  // ============================================
  // FLOWS API
  // ============================================

  async getFlows(): Promise<Flow[]> {
    try {
      const response = await fetch(`${API_BASE}/flows`);
      const data = await response.json();
      return data.flows || [];
    } catch (error) {
      console.error('Error getting flows:', error);
      return [];
    }
  }

  async getFlow(flowId: string): Promise<Flow | null> {
    try {
      const response = await fetch(`${API_BASE}/flows/${flowId}`);
      const data = await response.json();
      return data.flow || null;
    } catch (error) {
      console.error('Error getting flow:', error);
      return null;
    }
  }

  async createFlow(flow: Partial<Flow>): Promise<Flow | null> {
    try {
      const response = await fetch(`${API_BASE}/flows`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(flow)
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Flow creato');
        return data.flow;
      }
      return null;
    } catch (error) {
      console.error('Error creating flow:', error);
      toast.error('Errore creazione flow');
      return null;
    }
  }

  async updateFlow(flowId: string, updates: Partial<Flow>): Promise<Flow | null> {
    try {
      const response = await fetch(`${API_BASE}/flows/${flowId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Flow aggiornato');
        return data.flow;
      }
      return null;
    } catch (error) {
      console.error('Error updating flow:', error);
      toast.error('Errore aggiornamento flow');
      return null;
    }
  }

  async deleteFlow(flowId: string): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE}/flows/${flowId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Flow eliminato');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error deleting flow:', error);
      toast.error('Errore eliminazione flow');
      return false;
    }
  }

  async toggleFlow(flowId: string): Promise<Flow | null> {
    try {
      const response = await fetch(`${API_BASE}/flows/${flowId}/toggle`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        const status = data.flow.isActive ? 'attivato' : 'disattivato';
        toast.success(`Flow ${status}`);
        return data.flow;
      }
      return null;
    } catch (error) {
      console.error('Error toggling flow:', error);
      toast.error('Errore cambio stato flow');
      return null;
    }
  }

  // ============================================
  // LOGS
  // ============================================

  async getLogs(limit: number = 100, level?: string): Promise<Log[]> {
    try {
      let url = `${API_BASE}/logs?limit=${limit}`;
      if (level) url += `&level=${level}`;
      const response = await fetch(url);
      const data = await response.json();
      return data.logs || [];
    } catch (error) {
      console.error('Error getting logs:', error);
      return [];
    }
  }

  // ============================================
  // CONFIGURATION
  // ============================================

  async getConfig(): Promise<any> {
    try {
      const response = await fetch(`${API_BASE}/config`);
      const data = await response.json();
      return data.config || {};
    } catch (error) {
      console.error('Error getting config:', error);
      return {};
    }
  }

  async updateConfig(config: { problemGroup?: string; memoGroup?: string }): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE}/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Configurazione aggiornata');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error updating config:', error);
      toast.error('Errore aggiornamento configurazione');
      return false;
    }
  }

  // ============================================
  // SESSION MANAGEMENT
  // ============================================

  async logout(): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE}/logout`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        toast.success('Disconnesso da WhatsApp');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error logging out:', error);
      toast.error('Errore disconnessione');
      return false;
    }
  }

  async restart(): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE}/restart`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        toast.success('Client in riavvio...');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error restarting:', error);
      toast.error('Errore riavvio');
      return false;
    }
  }
}

// Singleton instance
const whatsappService = new WhatsAppService();
export default whatsappService;
